import type { ReactNode } from 'react';
import type { Agent, AgentSessionEntry, AgentTeamEntry, AgentWorkspaceEntry } from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';

export interface AgentDetailProps {
  agent: Agent;
  sessions: AgentSessionEntry[];
  hasMoreSessions: boolean;
  loadingMoreSessions: boolean;
  onLoadMoreSessions: () => void;
  workspaces: AgentWorkspaceEntry[];
  teams: AgentTeamEntry[];
  newChatPending: boolean;
  onNewChat: () => void;
  onRename: () => void;
  onDelete: () => void;
  onOpenSession: (sessionId: string, participantId: string | null) => void;
  onOpenPersona: (personaId: string) => void;
}

function DetailSection({
  testId,
  title,
  isEmpty,
  emptyText,
  children,
}: {
  testId: string;
  title: string;
  isEmpty: boolean;
  emptyText: string;
  children: ReactNode;
}) {
  return (
    <section data-testid={testId} aria-label={title} className="flex flex-col gap-2">
      <h2 className="text-title-2 text-foreground">{title}</h2>
      {isEmpty ? (
        <p data-testid={`${testId}-empty`} className="rounded-lg bg-card px-3 py-4 text-body text-muted-foreground">
          {emptyText}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg bg-card">{children}</ul>
      )}
    </section>
  );
}

function formatLastActive(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function AgentDetail({
  agent,
  sessions,
  hasMoreSessions,
  loadingMoreSessions,
  onLoadMoreSessions,
  workspaces,
  teams,
  newChatPending,
  onNewChat,
  onRename,
  onDelete,
  onOpenSession,
  onOpenPersona,
}: AgentDetailProps) {
  const deleted = agent.deleted_at !== null;
  const runningCount = sessions.filter((s) => s.running).length;

  return (
    <div data-testid="agent-detail" data-agent-id={agent.id} className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 data-testid="agent-detail-name" className="truncate text-title-1 text-foreground">
              {agent.name}
            </h1>
            {agent.is_default && (
              <Badge data-testid="agent-detail-default-badge" variant="secondary">Default</Badge>
            )}
            {deleted && (
              <Badge data-testid="agent-detail-deleted-badge" variant="destructive">Deleted</Badge>
            )}
            {!deleted && runningCount > 0 && (
              <Badge data-testid="agent-detail-active-badge" variant="info">
                Active in {runningCount} {runningCount === 1 ? 'session' : 'sessions'}
              </Badge>
            )}
          </div>
          <p className="text-body text-muted-foreground">
            Runs persona{' '}
            <button
              type="button"
              data-testid="agent-detail-persona"
              className="font-medium text-link hover:underline"
              onClick={() => onOpenPersona(agent.persona_id)}
            >
              {agent.persona_name}
            </button>
          </p>
          {agent.description && (
            <p data-testid="agent-detail-description" className="text-body text-foreground-secondary">
              {agent.description}
            </p>
          )}
        </div>
        {!deleted && (
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="agent-new-chat" onClick={onNewChat} disabled={newChatPending}>
              {newChatPending ? 'Starting chat…' : 'New chat with this Agent'}
            </Button>
            <Button data-testid="agent-rename" variant="outline" onClick={onRename}>
              Rename
            </Button>
            <Button data-testid="agent-delete" variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          </div>
        )}
      </header>

      <DetailSection
        testId="agent-detail-sessions"
        title="Sessions"
        isEmpty={sessions.length === 0}
        emptyText="This Agent has not taken part in any sessions yet."
      >
        {sessions.map((session) => (
          <li key={`${session.session_id}:${session.role}`}>
            <button
              type="button"
              data-testid={`agent-session-${session.session_id}`}
              data-role={session.role}
              data-running={session.running ? 'true' : 'false'}
              data-participant-id={session.participant_id ?? undefined}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent"
              onClick={() => onOpenSession(session.session_id, session.participant_id)}
            >
              <span className="min-w-0 flex-1 truncate text-body font-medium text-foreground">
                {session.session_name}
              </span>
              <Badge variant={session.role === 'primary' ? 'default' : 'outline'}>
                {session.role === 'primary' ? 'Primary' : 'Subagent'}
              </Badge>
              {session.running ? (
                <span className="flex items-center gap-1 text-caption text-info-text">
                  <span aria-hidden className="h-2 w-2 rounded-full bg-info" />
                  Running
                </span>
              ) : (
                <span className="text-caption text-muted-foreground">{formatLastActive(session.last_active)}</span>
              )}
            </button>
          </li>
        ))}
      </DetailSection>

      {hasMoreSessions && (
        <div>
          <Button
            data-testid="agent-detail-sessions-more"
            variant="outline"
            size="sm"
            onClick={onLoadMoreSessions}
            disabled={loadingMoreSessions}
          >
            {loadingMoreSessions ? 'Loading…' : 'Show older sessions'}
          </Button>
        </div>
      )}

      <DetailSection
        testId="agent-detail-workspaces"
        title="Workspaces"
        isEmpty={workspaces.length === 0}
        emptyText="None of this Agent's sessions ran in a workspace."
      >
        {workspaces.map((workspace) => (
          <li
            key={workspace.workspace_id}
            data-testid={`agent-workspace-${workspace.workspace_id}`}
            className="px-3 py-2 text-body text-foreground"
          >
            {workspace.name}
          </li>
        ))}
      </DetailSection>

      <DetailSection
        testId="agent-detail-teams"
        title="Teams"
        isEmpty={teams.length === 0}
        emptyText="This Agent staffs no team positions."
      >
        {teams.map((team) => (
          <li
            key={team.position_id}
            data-testid={`agent-team-${team.position_id}`}
            className="flex items-center gap-3 px-3 py-2 text-body"
          >
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{team.team_name}</span>
            <span className="text-caption text-muted-foreground">{team.position_title}</span>
          </li>
        ))}
      </DetailSection>
    </div>
  );
}
